/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Autocomplete from '../../components/SimpleAutocomplete';
import HTTPError from '@scale/frontend-lib/httpError';
import { AutocompleteOption } from 'design-system/Autocomplete/Autocomplete';
import { useOCPStore } from '../hooks/useOCPStore';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import PanelEntry from './PanelEntry';
import {
  OnboardingStepType,
  OnboardingFlowStepNode,
  ScheduleInterviewStepConfig,
  InterviewOutcomeStepConfig,
  AsyncInterviewStepConfig,
} from '@scale/shared/onboardingControlPanel';
import { Icon, Typography } from '@scale/scaleui';
import CopyToClipboard from 'react-copy-to-clipboard';
import { InterviewMode } from '@scale/shared/interview';

const InterviewConfigAutocomplete: React.FC<{
  required?: boolean;
  stepType: OnboardingStepType;
}> = ({ required, stepType }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget,
    setDetailTarget: state.setDetailTarget,
  }));
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [interviewId, setInterviewId] = useState<string>(
    (
      (detailTarget as OnboardingFlowStepNode)?.config as
        | ScheduleInterviewStepConfig
        | InterviewOutcomeStepConfig
        | AsyncInterviewStepConfig
    )?.interviewId ?? '',
  );
  const [searchText, setSearchText] = useState<string>('');
  const uneditable = currentVersion !== -1;
  const [options, setOptions] = useState<AutocompleteOption[]>([]); // Declare options here

  useEffect(() => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          type: stepType,
          interviewId,
        },
      });
      setDetailTarget(newDetailTarget);
    }
  }, [interviewId]);

  const handleSelectOption = (option: AutocompleteOption) => {
    setInterviewId(option.id);
    setSearchText('');
  };

  const handleInputChange = (text: string) => {
    setSearchText(text);
  };

  useEffect(() => {
    const fetchInterviewConfigs = async (mode: InterviewMode) => {
      const newOptions = await apiFetch(null, `corp-api/interview/configs/${mode}`).then(
        response =>
          response.map(({ _id, name }: { _id: string; name: string }) => ({
            id: _id,
            title: name,
          })) as AutocompleteOption[],
      );

      setOptions(newOptions);

      const interviewId = (
        (detailTarget as OnboardingFlowStepNode)?.config as
          | ScheduleInterviewStepConfig
          | InterviewOutcomeStepConfig
          | AsyncInterviewStepConfig
      )?.interviewId;

      if (interviewId && newOptions.some(option => option.id === interviewId)) {
        setInterviewId(interviewId);
      } else {
        setInterviewId('');
      }
    };

    fetchInterviewConfigs(
      stepType === OnboardingStepType.AsyncInterview ? InterviewMode.ASYNC : InterviewMode.LIVE,
    ).catch(error => {
      defaultApiFetchErrorHandler(error as HTTPError, { handleHttp: true });
      setOptions([]);
    });
  }, [detailTarget]);

  const filteredOptions = searchText
    ? options.filter(
        option =>
          option.title.toLowerCase().includes(searchText.toLowerCase()) ||
          option.id.toLowerCase().startsWith(searchText.toLowerCase()),
      )
    : options;

  return (
    <PanelEntry title={`Interview${required ? '*' : ''}`}>
      {!!options.length && interviewId && (
        <Typography.Paragraph3 className="ml-1">
          Interview Name: {options.find(option => option.id === interviewId)?.title}
        </Typography.Paragraph3>
      )}
      <div className="flex gap-1 items-center">
        {interviewId && (
          <>
            <Typography.Paragraph3 className="ml-1">
              Interview ID: {interviewId}
            </Typography.Paragraph3>
            <CopyToClipboard text={interviewId}>
              <Icon icon="copy" variant="neutral" size="sm" />
            </CopyToClipboard>
          </>
        )}
      </div>
      <Autocomplete
        className="w-full"
        options={filteredOptions}
        value={{ id: interviewId, title: searchText }}
        setSelectedOption={handleSelectOption}
        onInputChange={handleInputChange}
        disabled={uneditable}
        placeholder="Search interview configs by name or ID..."
      />
    </PanelEntry>
  );
};
export default InterviewConfigAutocomplete;
